import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { OUTCOME_PATH_LABELS, SOLUTION_STATUS_LABELS, VER_STAGES, VER_STAGE_GUIDE, VER_STAGE_LABELS, hasRoleAtLeast } from '@raro/domain';
import { Breadcrumbs, Button, EmptyState, Label, Notice, Serif, Tag, Textarea } from '@raro/ui';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { ContextBase } from '../components/ContextBase';
import { CycleScope } from '../components/CycleScope';
import { useAiStatus } from '../lib/ai';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDate, formatMoney, stageLabel } from '../lib/format';
import type { Cycle, CycleSynthesis, Me, ProjectDetail, VerWorkflow } from '../lib/types';

const STAGE_LINK: Record<string, string> = { observar: 'sessoes', tornar_visivel: 'machine', identificar_o_comum: 'observacoes', contrariar: 'contradicoes', encontrar: 'hipoteses', provar: 'iniciativas' };

export function ProjetoVerPage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const { me } = useAuth();
  const status = useAiStatus();
  const project = useQuery({ queryKey: ['project', id], queryFn: () => api.get<ProjectDetail>(`/projects/${id}`) });
  const ver = useQuery({ queryKey: ['ver', id], queryFn: () => api.get<VerWorkflow>(`/projects/${id}/ver`) });
  const [editing, setEditing] = useState<CycleSynthesis['content'] | null>(null);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['ver', id] });
    void qc.invalidateQueries({ queryKey: ['project', id] });
    void qc.invalidateQueries({ queryKey: ['ver-overview'] });
  };
  const advance = useMutation({ mutationFn: (stage: string) => api.patch(`/cycles/${ver.data!.cycle!.id}`, { stage }), onSuccess: invalidate });
  const synthesize = useMutation({ mutationFn: () => api.post<CycleSynthesis>(`/cycles/${ver.data!.cycle!.id}/synthesize`), onSuccess: invalidate });
  const review = useMutation({ mutationFn: (body: Record<string, unknown>) => api.patch(`/syntheses/${ver.data!.synthesis!.id}`, body), onSuccess: () => { setEditing(null); invalidate(); } });
  const close = useMutation({ mutationFn: () => api.post(`/cycles/${ver.data!.cycle!.id}/close`), onSuccess: invalidate });
  const newCycle = useMutation({ mutationFn: () => api.post(`/projects/${id}/cycles`, { status: 'active', stage: 'observar', startsAt: new Date().toISOString() }), onSuccess: invalidate });

  if (project.isPending || ver.isPending) return <div className="rr-page rr-faint">carregando…</div>;
  if (!project.data || !ver.data) return <div className="rr-page"><Notice tone="error">não encontramos este projeto.</Notice></div>;
  const p = project.data;
  const w = ver.data;
  const role = roleFor(me, p.organizationId);
  const canEdit = hasRoleAtLeast(role, 'analyst');
  const canClose = hasRoleAtLeast(role, 'partner');
  const c = w.cycle;
  const s = w.synthesis;
  const sol = w.solution;
  const solutionApproved = Boolean(sol && (sol.status === 'approved' || sol.status === 'published'));
  const llm = Boolean(status.data?.llm);

  return (
    <>
      <div className="rr-topbar">
        <Breadcrumbs items={[{ label: 'VER', render: (l) => <Link to="/ver">{l}</Link> }, { label: p.organization.name }, { label: p.name, render: (l) => <Link to={`/projetos/${id}`}>{l}</Link> }, { label: c?.name ?? 'ciclo' }]} />
        <div className="rr-row">
          {c && <Tag tone={c.status === 'active' ? 'accent' : 'outline'}>{c.status === 'active' ? 'ciclo ativo' : c.status === 'closed' ? 'ciclo encerrado' : 'planejado'}</Tag>}
          {canEdit && (!c || c.status === 'closed') && <Button size="sm" variant="primary" onClick={() => newCycle.mutate()} disabled={newCycle.isPending}>iniciar ciclo {String((w.cycles[0]?.number ?? 0) + 1).padStart(2, '0')}</Button>}
        </div>
      </div>

      <div className="rr-page rr-page--tight rr-stack rr-stack--7">
        <header className="rr-stack rr-stack--3">
          <Label>VER · {c?.name ?? 'sem ciclo'}</Label>
          <h1 className="rr-display">{p.organization.name.toLowerCase()}: <Serif italic>{c?.stage ? VER_STAGE_GUIDE[c.stage].question.toLowerCase() : 'o que existe?'}</Serif></h1>
          {c && <p className="rr-heading rr-muted">{c.question ?? p.currentQuestion ?? <span className="rr-faint">sem pergunta: defina no escopo</span>}</p>}
          {c?.position && (
            <div className="rr-stack rr-stack--2">
              <Label tone="accent">A posição que fecha o ciclo</Label>
              <p className="rr-heading"><Serif italic>{c.position}</Serif></p>
              {c.outcomePath && <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>caminho: {OUTCOME_PATH_LABELS[c.outcomePath]}</span>}
            </div>
          )}
          {c && <p className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>etapa atual: {stageLabel(c.stage)}</p>}
          {w.budget && (w.budget.project.estimated > 0 || w.budget.project.actual > 0) && (
            <p className="rr-muted" style={{ fontSize: 'var(--rr-text-sm)' }}>
              {w.budget.cycle && w.budget.cycle.initiatives > 0 ? <>este ciclo: <b style={{ color: 'var(--rr-text)' }}>{w.budget.cycle.initiatives} iniciativa(s)</b> com estimativa de produção de <b style={{ color: 'var(--rr-accent)' }}>{formatMoney(w.budget.cycle.estimated)}</b>{w.budget.cycle.actual > 0 ? <> · realizado {formatMoney(w.budget.cycle.actual)}</> : null} · </> : null}
              projeto: {formatMoney(w.budget.project.estimated)} estimado{w.budget.project.actual > 0 ? ` · ${formatMoney(w.budget.project.actual)} realizado` : ''}
            </p>
          )}
        </header>

        <ContextBase projectId={id} />
        {c && <CycleScope projectId={id} cycle={c} team={w.team ?? []} canEdit={canEdit} canSell={canClose} />}

        <div className="rr-stages">
          {w.stages.map((st, i) => (
            <div key={st.stage} className={`rr-stage rr-stage--${st.state}`}>
              <div className="rr-stage__num">{String(i + 1).padStart(2, '0')}<span className={`rr-dot ${st.state === 'current' ? 'rr-dot--active' : st.state === 'done' ? '' : 'rr-dot--hollow'}`} /></div>
              <div className="rr-stack rr-stack--2">
                <div className="rr-row rr-row--wrap" style={{ gap: 'var(--rr-space-3)' }}>
                  <span className="rr-stage__name">{VER_STAGE_LABELS[st.stage]}</span>
                  {st.state === 'current' && <Tag tone="accent">agora</Tag>}
                </div>
                <span className="rr-stage__q">{VER_STAGE_GUIDE[st.stage].question}</span>
                <span className="rr-stage__does">{VER_STAGE_GUIDE[st.stage].does}</span>
                <div className="rr-row" style={{ gap: 'var(--rr-space-3)', marginTop: 4 }}>
                  <Link to={`/projetos/${id}?s=${STAGE_LINK[st.stage]}`} className="rr-action rr-action--bare" style={{ fontSize: 'var(--rr-text-xs)' }}>abrir {STAGE_LINK[st.stage]} →</Link>
                  {canEdit && c?.status === 'active' && st.state === 'current' && i < VER_STAGES.length - 1 && <Button size="sm" variant="quiet" onClick={() => advance.mutate(VER_STAGES[i + 1]!)} disabled={advance.isPending}>avançar para {VER_STAGE_LABELS[VER_STAGES[i + 1]!].toLowerCase()} →</Button>}
                  {canEdit && c?.status === 'active' && st.state === 'done' && <Button size="sm" variant="quiet" onClick={() => advance.mutate(st.stage)} disabled={advance.isPending}>voltar aqui</Button>}
                </div>
              </div>
              <div className="rr-stage__stats">
                {st.signals.map((sg) => <div key={sg.label} className="rr-stage__stat"><b>{sg.value}</b>{sg.label}</div>)}
              </div>
            </div>
          ))}
        </div>

        {c && (
          <section className="rr-section">
            <div className="rr-section__head">
              <Label>A Solução</Label>
              <Link to={`/projetos/${id}?s=solucao`} className="rr-action rr-action--bare" style={{ fontSize: 'var(--rr-text-xs)' }}>abrir a solução →</Link>
            </div>
            {!sol && <EmptyState title="o ciclo ainda não tem Solução.">o VER termina numa posição executável e aferível. a Machine rascunha a partir das hipóteses seguidas; um sócio aprova e escolhe o caminho.</EmptyState>}
            {sol && (
              <div className="rr-solution rr-solution--compact">
                <div className="rr-row" style={{ fontSize: 'var(--rr-text-xs)', color: 'var(--rr-text-3)' }}>
                  <Tag tone={solutionApproved ? 'accent' : 'outline'}>{SOLUTION_STATUS_LABELS[sol.status]}</Tag>
                  <span>v{sol.version} · {sol.claims.filter((x) => x.humanStatus === 'accepted').length} afirmações aceitas · {sol.claims.filter((x) => x.humanStatus === 'under_review').length} a revisar</span>
                </div>
                <p className="rr-solution__position"><Serif italic>{sol.position}</Serif></p>
              </div>
            )}
          </section>
        )}

        {c && (
          <section className="rr-section">
            <div className="rr-section__head">
              <Label>Retrospectiva do ciclo</Label>
              <div className="rr-row">
                {canEdit && <Button size="sm" variant={s ? 'quiet' : 'primary'} onClick={() => synthesize.mutate()} disabled={!llm || synthesize.isPending}>{synthesize.isPending ? 'a Machine está lendo o ciclo…' : s ? 'gerar de novo' : 'gerar síntese →'}</Button>}
                {s && s.status === 'draft' && canClose && !editing && <Button size="sm" variant="primary" onClick={() => review.mutate({ status: 'reviewed' })} disabled={review.isPending}>revisada, pode guardar</Button>}
                {s && s.status !== 'draft' && c.status === 'active' && canClose && <Button size="sm" variant="primary" onClick={() => close.mutate()} disabled={close.isPending || !solutionApproved} title={solutionApproved ? undefined : 'aprove a Solução antes de encerrar'}>encerrar ciclo</Button>}
              </div>
            </div>
            {status.data && !llm && <p className="rr-field__error">sem modelo configurado. a síntese pode ser escrita à mão depois de gerada, mas o rascunho vem da Machine.</p>}
            {(synthesize.isError || close.isError || review.isError) && <Notice tone="error">{((synthesize.error ?? close.error ?? review.error) as Error).message}</Notice>}
            {c.status === 'active' && canClose && s && s.status !== 'draft' && !solutionApproved && <p className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>para encerrar: Solução aprovada por sócio + retrospectiva revisada.</p>}
            {!s && <EmptyState title="o ciclo ainda não tem retrospectiva.">o que sabíamos, o que decidimos, o que aconteceu. três meses depois, alguém precisa entender. gerada depois dos efeitos.</EmptyState>}
            {s && !editing && (
              <div className="rr-synth">
                <div className="rr-row rr-row--between">
                  <span className="rr-row" style={{ fontSize: 'var(--rr-text-xs)', color: 'var(--rr-text-3)' }}>
                    <Tag tone={s.status === 'draft' ? 'outline' : 'accent'}>{s.status === 'draft' ? 'rascunho da Machine' : s.status === 'reviewed' ? 'revisada pela Raro' : 'publicada'}</Tag>
                    <span>{s.model ?? 'editada à mão'} · {formatDate(s.createdAt)}</span>
                  </span>
                  {canEdit && <Button size="sm" variant="quiet" onClick={() => setEditing({ ...s.content })}>editar</Button>}
                </div>
                <SynthBlock title="O que sabíamos"><p>{s.content.whatWeKnew}</p></SynthBlock>
                <SynthBlock title="O que vimos"><ul>{s.content.whatWeSaw.map((x, i) => <li key={i}>{x}</li>)}</ul></SynthBlock>
                <SynthBlock title="O que decidimos"><ul>{s.content.whatWeDecided.map((x, i) => <li key={i}>{x}</li>)}</ul></SynthBlock>
                <SynthBlock title="O que aconteceu"><p>{s.content.whatHappened}</p></SynthBlock>
                <SynthBlock title="O que aprendemos"><ul>{s.content.whatWeLearned.map((x, i) => <li key={i}><Serif>{x}</Serif></li>)}</ul></SynthBlock>
                <SynthBlock title="O que segue aberto"><ul>{s.content.openQuestions.map((x, i) => <li key={i}>{x}</li>)}</ul></SynthBlock>
                <SynthBlock title="Próximo VER"><p>{s.content.nextCycle}</p></SynthBlock>
              </div>
            )}
            {s && editing && (
              <div className="rr-synth">
                {(['whatWeKnew', 'whatHappened', 'nextCycle'] as const).map((k) => (
                  <div key={k} className="rr-synth__block"><Label>{TITLES[k]}</Label><Textarea rows={3} value={editing[k]} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })} /></div>
                ))}
                {(['whatWeSaw', 'whatWeDecided', 'whatWeLearned', 'openQuestions'] as const).map((k) => (
                  <div key={k} className="rr-synth__block"><Label>{TITLES[k]} · uma por linha</Label><Textarea rows={4} value={editing[k].join('\n')} onChange={(e) => setEditing({ ...editing, [k]: e.target.value.split('\n').map((l) => l.trim()).filter(Boolean) })} /></div>
                ))}
                <div className="rr-row">
                  <Button variant="primary" onClick={() => review.mutate({ content: editing })} disabled={review.isPending}>salvar</Button>
                  <Button variant="quiet" onClick={() => setEditing(null)}>cancelar</Button>
                </div>
              </div>
            )}
          </section>
        )}

        {w.cycles.length > 1 && (
          <section className="rr-section">
            <Label>Ciclos anteriores{p.type === 'live' ? ' · um período por vez, cada um com a sua síntese' : ''}</Label>
            <div className="rr-stack rr-stack--2">
              {w.cycles.filter((x) => x.id !== c?.id).map((x) => <PastCycle key={x.id} cycle={x} />)}
            </div>
          </section>
        )}
      </div>
    </>
  );
}

function PastCycle({ cycle }: { cycle: Cycle }) {
  const [open, setOpen] = useState(false);
  const syn = useQuery({ queryKey: ['synthesis', cycle.id], queryFn: () => api.get<CycleSynthesis | null>(`/cycles/${cycle.id}/synthesis`), enabled: open });
  return (
    <div className="rr-stack rr-stack--2">
      <button type="button" className="rr-list-row" style={{ gridTemplateColumns: '1fr auto auto', textAlign: 'left', cursor: 'pointer', background: 'none', border: '1px solid var(--rr-border)', color: 'inherit', font: 'inherit' }} onClick={() => setOpen(!open)}>
        <span>{cycle.name}{cycle.question ? <span className="rr-faint"> · {cycle.question.slice(0, 80)}</span> : null}</span>
        <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>{cycle.startsAt ? formatDate(cycle.startsAt) : ''}{cycle.endsAt ? ` → ${formatDate(cycle.endsAt)}` : ''} · {cycle.status === 'closed' ? 'encerrado' : cycle.status}</span>
        <span className="rr-label">{open ? 'fechar' : 'síntese'}</span>
      </button>
      {open && (
        <div className="rr-synth" style={{ marginLeft: 'var(--rr-space-5)' }}>
          {syn.isPending && <span className="rr-faint">…</span>}
          {syn.isSuccess && !syn.data && <span className="rr-faint" style={{ fontSize: 'var(--rr-text-sm)' }}>este ciclo não tem síntese.</span>}
          {syn.data && (
            <>
              <div className="rr-row" style={{ fontSize: 'var(--rr-text-xs)', color: 'var(--rr-text-3)' }}><Tag tone={syn.data.status === 'draft' ? 'outline' : 'accent'}>{syn.data.status === 'draft' ? 'rascunho da Machine' : syn.data.status === 'reviewed' ? 'revisada' : 'publicada'}</Tag><span>{syn.data.model ?? 'editada à mão'} · {formatDate(syn.data.createdAt)}</span></div>
              <SynthBlock title="O que vimos"><ul>{syn.data.content.whatWeSaw.map((x, i) => <li key={i}>{x}</li>)}</ul></SynthBlock>
              <SynthBlock title="O que aconteceu"><p>{syn.data.content.whatHappened}</p></SynthBlock>
              <SynthBlock title="O que aprendemos"><ul>{syn.data.content.whatWeLearned.map((x, i) => <li key={i}><Serif>{x}</Serif></li>)}</ul></SynthBlock>
              <SynthBlock title="Próximo período"><p>{syn.data.content.nextCycle}</p></SynthBlock>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const TITLES = { whatWeKnew: 'O que sabíamos', whatWeSaw: 'O que vimos', whatWeDecided: 'O que decidimos', whatHappened: 'O que aconteceu', whatWeLearned: 'O que aprendemos', openQuestions: 'O que segue aberto', nextCycle: 'Próximo VER' } as const;

function SynthBlock({ title, children }: { title: string; children: React.ReactNode }) {
  return <div className="rr-synth__block"><Label>{title}</Label>{children}</div>;
}

function roleFor(me: Me | null, organizationId: string | undefined) {
  if (!me || !organizationId) return 'client' as const;
  const direct = me.memberships.find((m) => m.organizationId === organizationId)?.role;
  if (direct && me.internalRole) return hasRoleAtLeast(direct, me.internalRole) ? direct : me.internalRole;
  return direct ?? me.internalRole ?? ('client' as const);
}
